import React from 'react';
import styled from 'styled-components';
import { Icon } from '@tviklax/ui-components';
import { RouteInsight } from './types';

const Card = styled.div`
  background: white;
  border-radius: 16px;
  padding: 24px;
  box-shadow: ${props => props.theme.shadows.md};
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 24px;
`;

const Title = styled.h3`
  font-size: 1rem;
  font-weight: 700;
  color: ${props => props.theme.colors.gray800};
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Th = styled.th`
  text-align: left;
  font-size: 0.75rem;
  color: ${props => props.theme.colors.gray500};
  font-weight: 600;
  text-transform: uppercase;
  padding-bottom: 12px;
`;

const Td = styled.td`
  padding: 12px 0;
  border-bottom: 1px solid ${props => props.theme.colors.gray100};
  font-size: 0.875rem;
  color: ${props => props.theme.colors.gray800};
  font-weight: 500;
`;

const RiskIndicator = styled.div<{ $risk: 'low' | 'medium' | 'high' }>`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 0.75rem;
  font-weight: 700;
  color: ${props => {
    if (props.$risk === 'high') return props.theme.colors.error;
    if (props.$risk === 'medium') return props.theme.colors.warning;
    return props.theme.colors.success;
  }};
`;

const Dot = styled.span<{ $color: string }>`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background-color: ${props => props.$color};
`;

interface RouteInsightsProps {
  insights: RouteInsight[];
}

export const RouteInsights: React.FC<RouteInsightsProps> = ({ insights }) => {
  return (
    <Card>
      <Header>
        <Icon name="Globe" color="#64748b" size={20} />
        <Title>Geography & Lane Insights</Title>
      </Header>
      <Table>
        <thead>
          <tr>
            <Th>Route</Th>
            <Th>Avg Time</Th>
            <Th>Cost</Th>
            <Th>Risk</Th>
          </tr>
        </thead>
        <tbody>
          {insights.map((insight) => (
            <tr key={insight.route}>
              <Td>{insight.route}</Td>
              <Td>
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  {insight.deliveryTime}
                  {insight.trend === 'up' && <Icon name="TrendingUp" color="#ef4444" size={12} />}
                  {insight.trend === 'down' && <Icon name="TrendingDown" color="#10b981" size={12} />}
                </div>
              </Td>
              <Td>{insight.cost}</Td>
              <Td>
                <RiskIndicator $risk={insight.delayRisk}>
                  <Dot $color={
                    insight.delayRisk === 'high' ? '#ef4444' :
                      insight.delayRisk === 'medium' ? '#f59e0b' : '#10b981'
                  } />
                  {insight.delayRisk.toUpperCase()}
                </RiskIndicator>
              </Td>
            </tr>
          ))}
        </tbody>
      </Table>
    </Card>
  );
};
